import React from 'react';
import { Alert, Button, Card, Space, Tooltip, Typography } from 'antd';
import { BgColorsOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { catalogImageUrl } from '../../images';
import { variationLabel } from '../ingestionHelpers';
import { VariantAssetViewer } from './VariantAssetViewer';
import { MeshyGenConfig } from './MeshyGenConfig';
import { BuilderRunPanel } from './BuilderRunPanel';

const { Text } = Typography;

/**
 * One geometry group's model: the variants that differ only by color / texture
 * share it. The representative (the member with the model, else the first with
 * an image) is modelled here — the Opus chat, or Meshy's controls and viewer —
 * and "Texture rest" textures the other members from it once it is approved.
 */
export function ModelSection({
    item,
    title,
    group,
    representative,
    byVariant,
    opus,
    isBed,
    hasModel,
    onRebuild,
    onRegenerate,
    onRetexture,
    onModelConfigChange,
    onTextureRest,
    onUpload,
    onDownload,
    busy,
    texturing,
    labelOf,
}) {
    const rep = representative;
    const row = byVariant[rep.id];
    const repHasModel = hasModel(rep);
    const rest = group.filter((v) => v.id !== rep.id);
    const restToTexture = rest.filter((v) => !hasModel(v));
    const restLabels = restToTexture.map((v) => labelOf(v));
    const ready = restToTexture.length > 0 && repHasModel;
    const whyNot = !repHasModel ? 'Build or upload this model first' : 'Every variant of this model is textured';

    const modelSourceOptionsOf = () =>
        (item.variants || [])
            .filter((o) => o.id !== rep.id && hasModel(o))
            .map((o) => ({ variantId: o.id, label: labelOf(o), cutoutUrl: o.imageId ? catalogImageUrl(o.imageId, 512) : null }));

    return (
        <Card
            size="small"
            title={
                <Space wrap>
                    <span>{title}</span>
                    {rest.length > 0 && (
                        <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                            modelled on {labelOf(rep)}
                        </Text>
                    )}
                </Space>
            }
            extra={
                rest.length > 0 && (
                    <Space>
                        <Tooltip title={ready ? '' : whyNot}>
                            <Button
                                icon={<BgColorsOutlined />}
                                disabled={!ready || busy}
                                loading={texturing}
                                onClick={() => onTextureRest(rep)}
                            >
                                Texture rest
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                restToTexture.length > 0
                                    ? `This model will be used to texture ${restLabels.join(' and ')}`
                                    : `${rest.map((v) => labelOf(v)).join(' and ')} already have a model`
                            }
                        >
                            <QuestionCircleOutlined style={{ color: 'rgba(255,255,255,0.45)' }} />
                        </Tooltip>
                    </Space>
                )
            }
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {rep.meshyConfig?.lastError && <Alert type="error" showIcon message="Model generation failed" description={rep.meshyConfig.lastError} />}
                {opus ? (
                    <BuilderRunPanel
                        itemId={item.id}
                        variantId={rep.id}
                        run={rep.builderRun}
                        runs={rep.builderRuns}
                        hasModel={repHasModel}
                        onRebuild={(feedback) => onRebuild(rep.id, feedback)}
                        onUploadModel={(file) => onUpload(rep.id, file)}
                        busy={busy}
                        sourceLabel={(id) => {
                            const source = (item.variants || []).find((v) => v.id === id);
                            return source ? labelOf(source) : null;
                        }}
                    />
                ) : (
                    <>
                        <VariantAssetViewer
                            itemId={item.id}
                            variantId={rep.id}
                            assets={(row?.assets || []).filter((a) => a.kind === 'original')}
                            itemType={item.itemType}
                            modelsOnly
                            uploadAccept=".glb,.babylon"
                            uploadLabel="Upload 3D model"
                            onUpload={(file) => onUpload(rep.id, file)}
                            onDownload={(asset, name) => onDownload(asset, name, rep.id)}
                        />
                        <MeshyGenConfig
                            itemId={item.id}
                            variant={rep}
                            isBed={isBed}
                            onModelChange={(cfg) => onModelConfigChange(rep.id, cfg)}
                            onRegenerate={() => onRegenerate(rep.id)}
                            regenerating={busy}
                            onRetexture={(cfg) => onRetexture(rep.id, cfg)}
                            hasOwnModel={repHasModel}
                            modelSourceOptions={modelSourceOptionsOf()}
                            retexturing={texturing}
                            busy={busy || texturing}
                        />
                    </>
                )}
            </Space>
        </Card>
    );
}

export const sectionTitleOf = (item, variant, groupCount, groupLabel) =>
    groupCount > 1 ? groupLabel || variationLabel(item, variant) : 'Model';
